import { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import { getImage } from "redux/slices/getImageSlice";
import {
  getTaxStatus,
  getCompanyNameByBlockCode,
  base64ToArrayBuffer,
  getBadgeClassByPolicyStatus,
  getBadgeText,
} from "js/formatting";
import config from "config/config";

import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import PDFDialog from "components/ModalDialogs/PDFDialog";
import CardWithFields from "components/General/CardWithFields";
import * as fileSaver from "file-saver";
import Badge from "components/General/Badge";
import { ChatContext } from "context/Agent/ChatProvider";
import CardDisclaimerBase from "components/General/CardDisclaimerBase";

const PayoutCoverageAndDocuments = ({ policy, filteredDocuments }) => {
  const { t } = useTranslation();
  let dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [pdfSource, setPdfSource] = useState("");
  const [desc, setDesc] = useState("");
  const [pdfDialogState, setPdfDialogState] = useState(false);
  const [name, setName] = useState("");
  const { openChatWindow, setOpenChatWindow } = useContext(ChatContext);
  const AnnuityDocuments = () => (
    <>
      <EverlakeLoadingOverlay isLoading={loading}>
        {filteredDocuments?.some(
          (document) =>
            document?.document?.typeOfDocument?.toUpperCase() === "TAX"
        ) && (
          <>
            <div className="px-4 pb-3 xl:px-6 xl:pb-6">
              <div className="font-semibold mt-6 mb-1">
                {t("AuthAgentPolicyDetail-annuity-documents-title")}
              </div>
              <div className="ui-download-document">
                {filteredDocuments?.map((document, index) => {
                  if (
                    document?.document?.typeOfDocument?.toUpperCase() === "TAX"
                  ) {
                    return (
                      <div
                        className="inline-block p-2 border border-ui-gray-light-medium rounded"
                        key={`document${index}`}
                      >
                        <div className="text-xs font-semibold text-theme-text-secondary whitespace-nowrap">
                          {document?.document?.typeOfDocument?.toUpperCase() ===
                            "TAX" && t("FairMarketValueLetters-des")}
                        </div>
                        <div>
                          <button
                            className="ui-link-icon font-bold text-xs"
                            onClick={() => {
                              if (document?.name) {
                                setLoading(true);
                                dispatch(
                                  getImage({
                                    imageFileName: document.document.name,
                                    policyId: document.policy.contractID,
                                  })
                                )
                                  .then((response) => {
                                    setLoading(false);
                                    let byteArray = base64ToArrayBuffer(
                                      response.payload.document.contentStream
                                        .type
                                    );
                                    let blob = new Blob([byteArray], {
                                      type: "application/pdf",
                                    });
                                    fileSaver.saveAs(
                                      blob,
                                      `${document.document.name}.pdf`
                                    );
                                  })
                                  .catch((error) => {
                                    console.log("Error", error.message);
                                    setName(document?.document?.name);
                                    setPdfDialogState(true);
                                    setLoading(false);
                                    setDesc(document?.document?.description);
                                    setPdfSource("");
                                  });
                              }
                            }}
                          >
                            <svg
                              className="ui-icon w-4 h-4"
                              focusable="false"
                              aria-hidden="true"
                              role="presentation"
                            >
                              <use href="#ui-icon-document-download"></use>
                            </svg>
                            {t("AuthAgentPolicyDetail-download-button")}
                          </button>
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          </>
        )}
      </EverlakeLoadingOverlay>
    </>
  );

  const BadgeComponent = ({ policy }) => {
    return (
      <>
        {policy?.status && policy?.status !== "" && (
          <div className="hidden md:block">
            <Badge
              status={getBadgeClassByPolicyStatus(policy)}
              statusText={getBadgeText(policy)}
              srPrefix="Policy"
            />
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <CardWithFields
        additionalContent={
          <>
            <AnnuityDocuments />{" "}
            <CardDisclaimerBase
              disclaimer={
                <Trans
                  i18nKey={
                    "AuthAgentPolicyDetail-coverage-documents-annualstmt-disclaimer"
                  }
                  components={{
                    button1: (
                      <button
                        onClick={() => setOpenChatWindow(!openChatWindow)}
                        className="ui-btn-hyperlink text-xs font-semibold"
                      ></button>
                    ),
                  }}
                ></Trans>
              }
            />
          </>
        }
        additionalChildComponent={<BadgeComponent policy={policy} />}
        badgeClass={getBadgeClassByPolicyStatus(policy)}
        ariaLabel={t("AuthAgentPolicyDetail-card-component-aria-label")}
        cardTitle={t("AuthAgentPolicyDetail-coverage-documents-title")}
        cardFields={[
          {
            title: t("AuthAgentPolicyDetail-coverage-documents-label-1"),
            value: getCompanyNameByBlockCode(policy?.blockCode),
            hidden:
              config.ENABLE_SHOW_COMPANY_NAME_ON_ANNUITY_POLICIES === "false",
          },
          {
            title: t("AuthAgentPolicyDetail-coverage-documents-label-2"),
            value: policy?.oldPolicy,
          },
          {
            title: t("AuthAgentPolicyDetail-coverage-documents-label-3"),
            value: getTaxStatus(policy?.tax),
          },
        ]}
      />
      <PDFDialog
        title={`${name ?? ""} ${t("AuthAgentPolicyDetail-pdf-preview-text")}`}
        description={desc}
        pdfSource={pdfSource}
        dialogState={pdfDialogState}
        setDialogState={setPdfDialogState}
      />
    </>
  );
};

export default PayoutCoverageAndDocuments;
